import React, { useState, useEffect } from 'react';
import { Globe, Plus, X, CheckCircle, AlertCircle, Key, Link, Settings, Trash2, Edit, Save, RefreshCw } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';

interface ExternalAgent {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  api_key?: string;
  auth_type: 'api_key' | 'bearer' | 'oauth' | 'none';
  capabilities: string[];
  platform: string;
  status: 'active' | 'inactive' | 'error';
  last_tested?: string;
  response_time?: number;
  success_rate?: number;
  created_at: string;
}

interface ExternalAgentRegistrationProps {
  isOpen: boolean;
  onClose: () => void;
}

const COMMON_PLATFORMS = [
  { id: 'openai-assistants', name: 'OpenAI Assistants API', endpoint: 'https://api.openai.com/v1/assistants' },
  { id: 'anthropic-claude', name: 'Anthropic Claude API', endpoint: 'https://api.anthropic.com/v1/messages' },
  { id: 'google-vertex', name: 'Google Vertex AI', endpoint: 'https://us-central1-aiplatform.googleapis.com/v1' },
  { id: 'azure-openai', name: 'Azure OpenAI', endpoint: 'https://your-resource.openai.azure.com/openai' },
  { id: 'custom-webhook', name: 'Custom Webhook', endpoint: 'https://your-agent-endpoint.com/webhook' },
  { id: 'zapier', name: 'Zapier Webhook', endpoint: 'https://hooks.zapier.com/hooks/catch' },
  { id: 'make', name: 'Make.com Webhook', endpoint: 'https://hook.integromat.com' }
];

const COMMON_CAPABILITIES = [
  'text_generation', 'question_answering', 'data_analysis', 'web_search',
  'email_sending', 'calendar_management', 'file_processing', 'image_generation',
  'code_generation', 'translation', 'summarization', 'sentiment_analysis'
];

export function ExternalAgentRegistration({ isOpen, onClose }: ExternalAgentRegistrationProps) {
  const [externalAgents, setExternalAgents] = useState<ExternalAgent[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<ExternalAgent | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newAgent, setNewAgent] = useState({
    name: '',
    description: '',
    endpoint: '',
    api_key: '',
    auth_type: 'api_key' as const,
    capabilities: [] as string[],
    platform: 'custom-webhook'
  });
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && user) {
      fetchExternalAgents();
    }
  }, [isOpen, user]);

  const fetchExternalAgents = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('external_agents')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setExternalAgents(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAgent = async () => {
    if (!newAgent.name || !newAgent.endpoint) {
      setError('Name and endpoint are required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const agentData = {
        ...newAgent,
        user_id: user?.id,
        status: 'active',
        created_at: new Date().toISOString()
      };

      if (editingAgent) {
        const { error } = await supabase
          .from('external_agents')
          .update(agentData)
          .eq('id', editingAgent.id);

        if (error) throw error;
        setSuccess('External agent updated successfully');
        setEditingAgent(null);
      } else {
        const { error } = await supabase
          .from('external_agents')
          .insert(agentData);

        if (error) throw error;
        setSuccess('External agent registered successfully');
        setShowAddForm(false);
      }

      // Reset form
      setNewAgent({
        name: '',
        description: '',
        endpoint: '',
        api_key: '',
        auth_type: 'api_key',
        capabilities: [],
        platform: 'custom-webhook'
      });

      fetchExternalAgents();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestAgent = async (agent: ExternalAgent) => {
    setTesting(agent.id);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/external-agent-tester`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agent_id: agent.id,
            test_message: 'Hello, this is a test message from Agent Store A2A protocol.'
          })
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Test successful! Response time: ${data.response_time}ms`);
        
        // Update agent status
        await supabase
          .from('external_agents')
          .update({
            status: 'active',
            last_tested: new Date().toISOString(),
            response_time: data.response_time,
            success_rate: data.success_rate || 100
          })
          .eq('id', agent.id);
        
        fetchExternalAgents();
      } else {
        throw new Error(data.error || 'Test failed');
      }
    } catch (err: any) {
      setError(`Test failed: ${err.message}`);
      
      // Update agent status to error
      await supabase
        .from('external_agents')
        .update({
          status: 'error',
          last_tested: new Date().toISOString()
        })
        .eq('id', agent.id);
      
      fetchExternalAgents();
    } finally {
      setTesting(null);
    }
  };

  const handleDeleteAgent = async (id: string) => {
    if (!confirm('Are you sure you want to delete this external agent?')) return;

    try {
      const { error } = await supabase
        .from('external_agents')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('External agent deleted successfully');
      fetchExternalAgents();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCapabilityToggle = (capability: string) => {
    const agent = editingAgent || newAgent;
    const capabilities = agent.capabilities.includes(capability)
      ? agent.capabilities.filter(c => c !== capability)
      : [...agent.capabilities, capability];

    if (editingAgent) {
      setEditingAgent({ ...editingAgent, capabilities });
    } else {
      setNewAgent({ ...newAgent, capabilities });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200';
      case 'inactive': return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const renderAgentForm = () => {
    const agent = editingAgent || newAgent;
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agent Name</label>
            <input
              type="text"
              value={agent.name}
              onChange={(e) => editingAgent 
                ? setEditingAgent({ ...editingAgent, name: e.target.value })
                : setNewAgent({ ...newAgent, name: e.target.value })
              }
              placeholder="My External Agent"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
            <select
              value={agent.platform}
              onChange={(e) => {
                const platform = COMMON_PLATFORMS.find(p => p.id === e.target.value);
                const updates = {
                  platform: e.target.value,
                  endpoint: platform?.endpoint || ''
                };
                
                editingAgent 
                  ? setEditingAgent({ ...editingAgent, ...updates })
                  : setNewAgent({ ...newAgent, ...updates });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {COMMON_PLATFORMS.map(platform => (
                <option key={platform.id} value={platform.id}>{platform.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={agent.description}
            onChange={(e) => editingAgent 
              ? setEditingAgent({ ...editingAgent, description: e.target.value })
              : setNewAgent({ ...newAgent, description: e.target.value })
            }
            placeholder="Describe what this external agent does..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">API Endpoint</label>
          <input
            type="url"
            value={agent.endpoint}
            onChange={(e) => editingAgent 
              ? setEditingAgent({ ...editingAgent, endpoint: e.target.value })
              : setNewAgent({ ...newAgent, endpoint: e.target.value })
            }
            placeholder="https://api.example.com/agent"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Authentication Type</label>
            <select
              value={agent.auth_type}
              onChange={(e) => editingAgent 
                ? setEditingAgent({ ...editingAgent, auth_type: e.target.value as any })
                : setNewAgent({ ...newAgent, auth_type: e.target.value as any })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="api_key">API Key</option>
              <option value="bearer">Bearer Token</option>
              <option value="oauth">OAuth</option>
              <option value="none">None</option>
            </select>
          </div>

          {agent.auth_type !== 'none' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {agent.auth_type === 'api_key' ? 'API Key' : 
                 agent.auth_type === 'bearer' ? 'Bearer Token' : 'OAuth Token'}
              </label>
              <input
                type="password"
                value={agent.api_key}
                onChange={(e) => editingAgent 
                  ? setEditingAgent({ ...editingAgent, api_key: e.target.value })
                  : setNewAgent({ ...newAgent, api_key: e.target.value })
                }
                placeholder="Enter your API key or token"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Capabilities</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {COMMON_CAPABILITIES.map(capability => (
              <label key={capability} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agent.capabilities.includes(capability)}
                  onChange={() => handleCapabilityToggle(capability)}
                  className="rounded text-blue-600"
                />
                <span className="text-sm text-gray-700">{capability.replace('_', ' ')}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              setShowAddForm(false);
              setEditingAgent(null);
              setNewAgent({
                name: '',
                description: '',
                endpoint: '',
                api_key: '',
                auth_type: 'api_key',
                capabilities: [],
                platform: 'custom-webhook'
              });
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAgent}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                {editingAgent ? 'Updating...' : 'Registering...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {editingAgent ? 'Update Agent' : 'Register Agent'}
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">External Agent Integration</h2>
              <p className="text-sm opacity-90">Connect agents from other platforms to your A2A network</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Status Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* Add/Edit Form */}
          {(showAddForm || editingAgent) && (
            <div className="mb-6 bg-gray-50 border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingAgent ? 'Edit External Agent' : 'Register External Agent'}
              </h3>
              {renderAgentForm()}
            </div>
          )}

          {/* External Agents List */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Your External Agents</h3>
            {!showAddForm && !editingAgent && (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Add External Agent
              </button>
            )}
          </div>

          {loading && !showAddForm && !editingAgent ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading external agents...</p>
            </div>
          ) : externalAgents.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No External Agents</h4>
              <p className="text-gray-600 mb-6">
                You haven't registered any external agents yet. Connect agents from other platforms to expand your A2A network.
              </p>
              {!showAddForm && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Register Your First External Agent
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {externalAgents.map(agent => (
                <div key={agent.id} className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-lg text-gray-900">{agent.name}</h4>
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(agent.status)}`}>
                          {agent.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{agent.description}</p>
                      <div className="text-xs text-gray-500">
                        <div>Platform: {COMMON_PLATFORMS.find(p => p.id === agent.platform)?.name || agent.platform}</div>
                        <div>Endpoint: {agent.endpoint}</div>
                        {agent.last_tested && (
                          <div>Last tested: {new Date(agent.last_tested).toLocaleDateString()}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Capabilities */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Capabilities</h5>
                    <div className="flex flex-wrap gap-1">
                      {agent.capabilities.map(capability => (
                        <span
                          key={capability}
                          className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs"
                        >
                          {capability.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  {agent.response_time && (
                    <div className="mb-4 grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500">Response Time</div>
                        <div className="font-medium">{agent.response_time}ms</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500">Success Rate</div>
                        <div className="font-medium">{agent.success_rate || 0}%</div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleTestAgent(agent)}
                      disabled={testing === agent.id}
                      className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center justify-center gap-1"
                    >
                      {testing === agent.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Test
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setEditingAgent(agent)}
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteAgent(agent.id)}
                      className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Integration Guide */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Integration Guide
            </h4>
            <div className="text-blue-800 text-sm space-y-2">
              <p><strong>Step 1:</strong> Choose the platform where your external agent is hosted</p>
              <p><strong>Step 2:</strong> Provide the agent's API endpoint and authentication details</p>
              <p><strong>Step 3:</strong> Define the agent's capabilities for proper A2A routing</p>
              <p><strong>Step 4:</strong> Test the connection to ensure it works properly</p>
              <p><strong>Step 5:</strong> Use the external agent in your A2A workflows</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}